import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, ArrowLeft, Camera, Plus, X, Check, Sparkles, Wifi, Shield } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AavaaHubVisual from "@/components/AavaaHubVisual";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Welcome", "Child Profile", "Conditions", "Observations", "Hub"];

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [childData, setChildData] = useState({ name: "", ageYears: 0, ageMonths: 0, gender: "" });
  const [conditions, setConditions] = useState([]);
  const [conditionsList, setConditionsList] = useState([]);
  const [suggestedObs, setSuggestedObs] = useState([]);
  const [selectedObs, setSelectedObs] = useState([]);
  const [customObs, setCustomObs] = useState("");
  const [customObsList, setCustomObsList] = useState([]);
  const [cameraData, setCameraData] = useState({ brand: "", deviceCode: "" });
  const [childId, setChildId] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user, setChild } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/observations/conditions`).then((r) => setConditionsList(r.data.conditions || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (step === 4 && conditions.length > 0) {
      axios.get(`${API}/observations/suggested?conditions=${conditions.join(",")}`).then((r) => {
        const all = r.data.suggested || [];
        setSuggestedObs(all);
        setSelectedObs(all);
      }).catch(() => {});
    }
  }, [step, conditions]);

  const createChild = async () => {
    setLoading(true);
    try {
      const ageStr = `${childData.ageYears} year${childData.ageYears !== 1 ? "s" : ""} ${childData.ageMonths} month${childData.ageMonths !== 1 ? "s" : ""}`;
      const res = await axios.post(`${API}/children`, { user_id: user.id, name: childData.name, age: ageStr, gender: childData.gender });
      setChildId(res.data.id);
      setStep(3);
    } catch (e) { toast.error("Could not save profile"); }
    setLoading(false);
  };

  const saveConditions = async () => {
    if (!childId) return;
    setLoading(true);
    try {
      await axios.put(`${API}/children/${childId}`, { conditions });
      setStep(4);
    } catch (e) { toast.error("Could not save conditions"); }
    setLoading(false);
  };

  const saveObservations = async () => {
    if (!childId) return;
    setLoading(true);
    try {
      await axios.put(`${API}/children/${childId}`, { observation_points: selectedObs, custom_observations: customObsList });
      setStep(5);
    } catch (e) { toast.error("Could not save observations"); }
    setLoading(false);
  };

  const finishOnboarding = async () => {
    if (!childId) return;
    setLoading(true);
    try {
      const res = await axios.put(`${API}/children/${childId}`, { camera_brand: "Aavaa Hub", camera_id: "hub-demo" });
      setChild(res.data);
      // Auto-ingest clips
      axios.post(`${API}/children/${childId}/ingest-clips`).catch(() => {});
      toast.success("Hub connected! Setting up your dashboard...");
      navigate("/app");
    } catch (e) { toast.error("Could not connect Hub"); }
    setLoading(false);
  };

  const toggleCondition = (c) => {
    setConditions((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };

  const toggleObs = (obs) => {
    setSelectedObs((prev) => prev.includes(obs) ? prev.filter((x) => x !== obs) : [...prev, obs]);
  };

  const addCustomObs = () => {
    if (customObs.trim()) {
      const obs = customObs.trim();
      setCustomObsList((prev) => [...prev, obs]);
      setSelectedObs((prev) => [...prev, obs]);
      setCustomObs("");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center p-6">
      <div className="w-full max-w-lg">
        {/* Progress */}
        <div className="flex items-center gap-1 mb-8 mt-4">
          {STEPS.map((s, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-500 ${i + 1 <= step ? "bg-[#8E9B82]" : "bg-[#E2DFD8]"}`} />
          ))}
        </div>

        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="animate-fade-up" data-testid="onboarding-welcome">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8E9B82]/20 to-[#D4A373]/20 flex items-center justify-center mx-auto mb-4">
                <Sparkles size={28} className="text-[#D4A373]" />
              </div>
              <h1 className="font-outfit text-4xl font-medium text-[#2F3630] tracking-tight mb-3">
                Welcome to aavaa
              </h1>
              <p className="font-manrope text-[#6B7264] text-base leading-relaxed max-w-sm mx-auto">
                Every parent deserves to know their child is thriving. Aavaa watches so you can breathe easy.
              </p>
            </div>
            <div className="bg-white rounded-[24px] p-6 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] border border-[#E2DFD8]/50 space-y-5 mb-8">
              {[
                { icon: Camera, color: "text-[#8E9B82]", bg: "bg-[#8E9B82]/10", title: "Connect your Aavaa Hub", desc: "Plug in once, clips flow automatically" },
                { icon: Sparkles, color: "text-[#D4A373]", bg: "bg-[#D4A373]/10", title: "Tell us what matters to you", desc: "Choose what Aavaa observes for your child" },
                { icon: Check, color: "text-[#8E9B82]", bg: "bg-[#8E9B82]/10", title: "Get meaningful summaries", desc: "Daily, weekly, and monthly insights backed by real clips" },
                { icon: ArrowRight, color: "text-[#D4A373]", bg: "bg-[#D4A373]/10", title: "Share with your circle", desc: "Family and therapists stay in the loop" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className={`w-9 h-9 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}>
                    <item.icon size={16} className={item.color} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="font-outfit text-sm font-medium text-[#2F3630]">{item.title}</p>
                    <p className="font-manrope text-xs text-[#6B7264]">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button data-testid="get-started-btn" onClick={() => setStep(2)} className="w-full rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-12 font-outfit font-medium text-base transition-all duration-300 hover:scale-[0.98] active:scale-95">
              Get Started <ArrowRight size={18} className="ml-2" />
            </Button>
            <p className="text-center text-xs text-[#6B7264]/50 mt-4 font-manrope">
              You're doing a wonderful thing for your child
            </p>
          </div>
        )}

        {/* Step 2: Child Profile */}
        {step === 2 && (
          <div className="animate-fade-up" data-testid="onboarding-profile">
            <button onClick={() => setStep(1)} className="flex items-center gap-1 text-[#6B7264] text-sm mb-6 hover:text-[#8E9B82] transition-colors" data-testid="back-to-welcome">
              <ArrowLeft size={16} /> Back
            </button>
            <h2 className="font-outfit text-3xl font-medium text-[#2F3630] mb-2">Tell us about your little one</h2>
            <p className="font-manrope text-[#6B7264] text-sm mb-6">This helps Aavaa personalize everything for your child</p>
            <div className="bg-white rounded-[24px] p-6 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] border border-[#E2DFD8]/50 space-y-5 mb-6">
              <div>
                <label className="text-sm font-manrope text-[#6B7264] tracking-wide uppercase mb-1 block">Child's name</label>
                <Input data-testid="child-name-input" value={childData.name} onChange={(e) => setChildData({ ...childData, name: e.target.value })} placeholder="e.g. Aanya" className="rounded-xl border-[#E2DFD8] h-12 focus:ring-2 focus:ring-[#8E9B82]/20 focus:border-[#8E9B82]" />
              </div>
              {/* Age Picker with +/- */}
              <div>
                <label className="text-sm font-manrope text-[#6B7264] tracking-wide uppercase mb-2 block">Age</label>
                <div className="flex gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-manrope text-[#6B7264] mb-1.5 text-center">Years</p>
                    <div className="flex items-center justify-center gap-1.5">
                      <button data-testid="age-years-minus" onClick={() => setChildData(d => ({...d, ageYears: Math.max(0, d.ageYears - 1)}))} className="w-9 h-9 rounded-lg bg-[#F7F5F0] border border-[#E2DFD8] flex items-center justify-center text-[#6B7264] hover:bg-[#E2DFD8] transition-colors font-outfit text-base font-medium flex-shrink-0">-</button>
                      <span className="w-8 text-center font-outfit text-xl font-medium text-[#2F3630]" data-testid="age-years-value">{childData.ageYears}</span>
                      <button data-testid="age-years-plus" onClick={() => setChildData(d => ({...d, ageYears: Math.min(18, d.ageYears + 1)}))} className="w-9 h-9 rounded-lg bg-[#F7F5F0] border border-[#E2DFD8] flex items-center justify-center text-[#6B7264] hover:bg-[#E2DFD8] transition-colors font-outfit text-base font-medium flex-shrink-0">+</button>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-manrope text-[#6B7264] mb-1.5 text-center">Months</p>
                    <div className="flex items-center justify-center gap-1.5">
                      <button data-testid="age-months-minus" onClick={() => setChildData(d => ({...d, ageMonths: Math.max(0, d.ageMonths - 1)}))} className="w-9 h-9 rounded-lg bg-[#F7F5F0] border border-[#E2DFD8] flex items-center justify-center text-[#6B7264] hover:bg-[#E2DFD8] transition-colors font-outfit text-base font-medium flex-shrink-0">-</button>
                      <span className="w-8 text-center font-outfit text-xl font-medium text-[#2F3630]" data-testid="age-months-value">{childData.ageMonths}</span>
                      <button data-testid="age-months-plus" onClick={() => setChildData(d => ({...d, ageMonths: Math.min(11, d.ageMonths + 1)}))} className="w-9 h-9 rounded-lg bg-[#F7F5F0] border border-[#E2DFD8] flex items-center justify-center text-[#6B7264] hover:bg-[#E2DFD8] transition-colors font-outfit text-base font-medium flex-shrink-0">+</button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Gender Selection */}
              <div>
                <label className="text-sm font-manrope text-[#6B7264] tracking-wide uppercase mb-2 block">Gender (optional)</label>
                <div className="flex gap-2">
                  {["Girl", "Boy", "Prefer not to say"].map((g) => (
                    <button
                      key={g}
                      data-testid={`gender-${g.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setChildData({ ...childData, gender: g })}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-manrope transition-all duration-200 ${
                        childData.gender === g
                          ? "bg-[#8E9B82] text-white shadow-sm"
                          : "bg-[#F7F5F0] text-[#6B7264] border border-[#E2DFD8] hover:border-[#8E9B82]/50"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Button data-testid="save-profile-btn" onClick={createChild} disabled={!childData.name || (childData.ageYears === 0 && childData.ageMonths === 0) || loading} className="w-full rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-12 font-outfit font-medium text-base transition-all duration-300 hover:scale-[0.98] active:scale-95">
              {loading ? "Saving..." : "Continue"} {!loading && <ArrowRight size={18} className="ml-2" />}
            </Button>
          </div>
        )}

        {/* Step 3: Conditions */}
        {step === 3 && (
          <div className="animate-fade-up" data-testid="onboarding-conditions">
            <button onClick={() => setStep(2)} className="flex items-center gap-1 text-[#6B7264] text-sm mb-6 hover:text-[#8E9B82] transition-colors" data-testid="back-to-profile">
              <ArrowLeft size={16} /> Back
            </button>
            <h2 className="font-outfit text-3xl font-medium text-[#2F3630] mb-2">Any specific areas of focus?</h2>
            <p className="font-manrope text-[#6B7264] text-sm mb-6">Select all that apply. Don't worry — this just helps Aavaa suggest the right things to watch for. You're in control.</p>
            <div className="space-y-2 mb-6 max-h-[50vh] overflow-y-auto no-scrollbar">
              {conditionsList.map((c) => (
                <button
                  key={c}
                  data-testid={`condition-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => toggleCondition(c)}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all duration-200 font-manrope text-sm ${
                    conditions.includes(c)
                      ? "bg-[#8E9B82]/10 border-[#8E9B82] text-[#2F3630] shadow-sm shadow-[#8E9B82]/10"
                      : "bg-white border-[#E2DFD8] text-[#6B7264] hover:border-[#8E9B82]/50 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${conditions.includes(c) ? "bg-[#8E9B82] border-[#8E9B82]" : "border-[#E2DFD8]"}`}>
                      {conditions.includes(c) && <Check size={12} className="text-white" strokeWidth={3} />}
                    </div>
                    {c}
                  </div>
                </button>
              ))}
            </div>
            <Button data-testid="save-conditions-btn" onClick={saveConditions} disabled={conditions.length === 0 || loading} className="w-full rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-12 font-outfit font-medium text-base transition-all duration-300 hover:scale-[0.98] active:scale-95">
              {loading ? "Saving..." : "Continue"} {!loading && <ArrowRight size={18} className="ml-2" />}
            </Button>
          </div>
        )}

        {/* Step 4: Observation Points */}
        {step === 4 && (
          <div className="animate-fade-up" data-testid="onboarding-observations">
            <button onClick={() => setStep(3)} className="flex items-center gap-1 text-[#6B7264] text-sm mb-6 hover:text-[#8E9B82] transition-colors" data-testid="back-to-conditions">
              <ArrowLeft size={16} /> Back
            </button>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={20} className="text-[#D4A373]" />
              <h2 className="font-outfit text-3xl font-medium text-[#2F3630]">Observation points</h2>
            </div>
            <p className="font-manrope text-[#6B7264] text-sm mb-6">Based on your child's profile, here are some observation points many parents want tracked.</p>
            <div className="space-y-2 mb-4 max-h-[40vh] overflow-y-auto no-scrollbar">
              {suggestedObs.map((obs) => (
                <button
                  key={obs}
                  data-testid={`obs-toggle-${obs.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => toggleObs(obs)}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all duration-200 font-manrope text-sm ${
                    selectedObs.includes(obs) ? "bg-[#8E9B82]/10 border-[#8E9B82] text-[#2F3630]" : "bg-white border-[#E2DFD8] text-[#6B7264]/60 line-through"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${selectedObs.includes(obs) ? "bg-[#8E9B82] border-[#8E9B82]" : "border-[#E2DFD8]"}`}>
                      {selectedObs.includes(obs) && <Check size={12} className="text-white" strokeWidth={3} />}
                    </div>
                    <span className={selectedObs.includes(obs) ? "" : "line-through"}>{obs}</span>
                  </div>
                </button>
              ))}
              {customObsList.map((obs) => (
                <div key={obs} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#D4A373]/10 border border-[#D4A373]/30">
                  <span className="font-manrope text-sm text-[#2F3630] flex-1">{obs}</span>
                  <button onClick={() => { setCustomObsList((p) => p.filter((x) => x !== obs)); setSelectedObs((p) => p.filter((x) => x !== obs)); }}>
                    <X size={14} className="text-[#6B7264]" />
                  </button>
                </div>
              ))}
            </div>
            {/* Custom observation input */}
            <div className="flex gap-2 mb-6">
              <Input
                data-testid="custom-obs-input"
                value={customObs}
                onChange={(e) => setCustomObs(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCustomObs()}
                placeholder="Add something specific you want tracked..."
                className="rounded-xl border-[#E2DFD8] h-10 text-sm flex-1 focus:ring-2 focus:ring-[#D4A373]/20 focus:border-[#D4A373]"
              />
              <Button data-testid="add-custom-obs-btn" onClick={addCustomObs} disabled={!customObs.trim()} variant="outline" className="rounded-xl h-10 px-3 border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373]/10">
                <Plus size={16} />
              </Button>
            </div>
            <Button data-testid="save-observations-btn" onClick={saveObservations} disabled={selectedObs.length === 0 || loading} className="w-full rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-12 font-outfit font-medium text-base transition-all duration-300 hover:scale-[0.98] active:scale-95">
              {loading ? "Saving..." : `Continue with ${selectedObs.length} points`} {!loading && <ArrowRight size={18} className="ml-2" />}
            </Button>
          </div>
        )}

        {/* Step 5: Connect Aavaa Hub */}
        {step === 5 && (
          <div className="animate-fade-up" data-testid="onboarding-hub">
            <button onClick={() => setStep(4)} className="flex items-center gap-1 text-[#6B7264] text-sm mb-6 hover:text-[#8E9B82] transition-colors" data-testid="back-to-observations-from-hub">
              <ArrowLeft size={16} /> Back
            </button>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={20} className="text-[#D4A373]" />
              <h2 className="font-outfit text-3xl font-medium text-[#2F3630]">Connect your Aavaa Hub</h2>
            </div>
            <p className="font-manrope text-[#6B7264] text-sm mb-4 leading-relaxed">
              The Hub is a small device that sits quietly in your child's play area and connects to your existing home camera. Plug it in once — you'll never have to touch it again.
            </p>

            <div className="bg-white rounded-[24px] p-5 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] border border-[#E2DFD8]/50 mb-5">
              <AavaaHubVisual />

              <div className="mt-2 pt-4 border-t border-[#E2DFD8]/60 space-y-3">
                {[
                  { icon: Wifi, title: "Works on your home Wi-Fi", desc: "No cables, no router changes" },
                  { icon: Camera, title: "Pairs with your existing camera", desc: "Tapo, Ring, Wyze & more" },
                  { icon: Shield, title: "Private by design", desc: "Only the daily summary leaves your home" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 animate-fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
                    <div className="w-8 h-8 rounded-xl bg-[#8E9B82]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon size={14} className="text-[#8E9B82]" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="font-outfit text-sm font-medium text-[#2F3630]">{item.title}</p>
                      <p className="font-manrope text-xs text-[#6B7264]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-[#8E9B82] animate-pulse" />
              <p className="font-manrope text-xs text-[#6B7264]">Hub detected on your network</p>
            </div>

            <Button data-testid="connect-hub-btn" onClick={finishOnboarding} disabled={loading} className="w-full rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-12 font-outfit font-medium text-base transition-all duration-300 hover:scale-[0.98] active:scale-95">
              {loading ? "Connecting..." : "Next"} {!loading && <ArrowRight size={18} className="ml-2" />}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
